import { listToCondition } from "@/shared/components/filters";
import type { Filter } from "@/shared/components/ResourceView";
import { InvocationStatus } from "@/shared/domain/actions";
import type { Condition } from "@/types";

/**
 * The invocations list's status filter: an `enum` over the statuses, whose
 * option values are the integer codes as strings. The query API compares
 * `last_status` against an integer column, so this filter is turned into a
 * condition here rather than by `composeFilters`, which would send strings.
 */
export const STATUS_FIELD = "last_status";

export const STATUS_OPTIONS = [
  { label: "Pending", value: String(InvocationStatus.Queued) },
  { label: "Provisioning", value: String(InvocationStatus.Scheduled) },
  { label: "Downloading", value: String(InvocationStatus.Downloading) },
  { label: "Processing", value: String(InvocationStatus.Processing) },
  { label: "Uploading", value: String(InvocationStatus.Uploading) },
  { label: "Completed", value: String(InvocationStatus.Completed) },
  { label: "Cancelled", value: String(InvocationStatus.Cancelled) },
  { label: "Failed", value: String(InvocationStatus.Failed) },
  { label: "Deadly", value: String(InvocationStatus.Deadly) },
];

/** Every status the picker offers, for resolving a negative selection to its complement. */
const FILTERABLE_STATUSES: InvocationStatus[] = STATUS_OPTIONS.map((option) =>
  Number(option.value),
);

/**
 * The status filter's selected statuses.
 *
 * `values` holds `{value, label}` options whose `value` is a status number as
 * a string (see `STATUS_OPTIONS`); the display label never crosses this
 * boundary. This mirrors the projection `filterToCondition` performs for
 * option-backed filters, the shared path this one bypasses to emit numbers.
 */
function selectedStatuses(filter: Filter | undefined): InvocationStatus[] {
  if (!filter || filter.type !== "enum") {
    return [];
  }
  return filter.values
    .map((option) => Number(option.value))
    .filter((value): value is InvocationStatus => value in InvocationStatus);
}

/** True for "is not", which keeps every status but the picked ones. */
function isNegative(filter: Filter | undefined): boolean {
  return filter?.comparator === "NONE_OF";
}

/**
 * The table's status filter as internal statuses, for the activity panel to
 * pick series by.
 *
 * A negative filter keeps every status except the selected ones, so the panel
 * draws the complement. Returning the selected statuses instead would draw
 * exactly the series the rows beneath the chart exclude.
 */
export function statusesOf(filter: Filter | undefined): InvocationStatus[] {
  const selected = selectedStatuses(filter);
  if (selected.length === 0) {
    return [];
  }
  if (isNegative(filter)) {
    return FILTERABLE_STATUSES.filter((status) => !selected.includes(status));
  }
  return selected;
}

/**
 * The status filter as a condition over the integer codes: "is any of" / "is
 * none of" send the picked statuses as an OR of `EQUALS` / an AND of
 * `NOT_EQUALS`, or the single comparison for one status (see
 * `listToCondition`). A status is never unset, so neither carries a null
 * check. An enum filter is only ever a list: a stored `EQUALS` is read as one
 * before it gets here (see `upgradeLegacyFilter`). `undefined` when no status
 * is picked.
 */
export function statusCondition(
  filter: Filter | undefined,
): Condition | undefined {
  const statuses = selectedStatuses(filter);
  if (
    !filter ||
    statuses.length === 0 ||
    (filter.comparator !== "ANY_OF" && filter.comparator !== "NONE_OF")
  ) {
    return undefined;
  }
  return listToCondition({
    field: STATUS_FIELD,
    comparator: filter.comparator,
    values: statuses,
    includeNull: false,
    nullChecks: false,
  });
}
